/**
 * The sign-in screen of the game (port addition: the original was a VK app and never asked who plays). It is shown
 * at start when no session is stored for this backend (FWP.net_boot: GameBackend.restoreSession() returned null) and
 * after a sign-out or an expired session (a clean restart, FWPDeps.restartReason). Three views on one panel:
 *  - landing: a welcome text, "Sign in" and "Sign up", the language switch (a pick restarts the game into this screen);
 *  - sign in / sign up: nickname and password fields, a show / hide toggle of the password, the submit button, a link
 *    to the other form and a back button to the landing view.
 *
 * Made only of the game's own art and text style (src/game/ui/controls.ts): the loading splash's background, the frame
 * of the popups (menu_tired, scaled to fit under the logo) with its decorations, the popup buttons and the small
 * buttons of the main screen, input fields like the settings screen's, Arial in the popups' colours.
 *
 * Behaviour:
 *  - the rules are the shared ones (src/shared/auth-rules.ts): the sign-up form checks while the player types, both
 *    forms check on submit, and a problem is shown under its field with the same text the server's reason gets;
 *  - the nickname field accepts only the allowed characters (restrict) and at most the longest nickname (maxChars);
 *  - Enter in the nickname field moves to the password field (the keyboard's key says "next"), Enter in the password
 *    field submits; Tab / Shift+Tab move between the fields, Escape goes back to the landing view;
 *  - while a request is in flight the submit button says "Please wait…" and every control is disabled, so a second
 *    submit is impossible; an error re-enables the form for a retry;
 *  - the password lives only in its field and in the one request: the field is emptied when the request is sent, and
 *    it is never copied into any state of the game or of this screen;
 *  - a nickname is never translated (it is data, not game text);
 *  - where the client offers it (FWPDeps.serverSetting: the native app, the dev server) the landing view names the
 *    server and opens a fourth view, "Server": its address field (checked like the client checks it), save (the game
 *    restarts against that server) and the build's default;
 *  - back (the Android back button, `back()`) leaves a form for the landing view like Escape.
 * E-mail sign-in (where the server offers it: GameServices.emailAvailable(), feature 'email'):
 *  - the sign-in form's first field takes a nickname or a confirmed e-mail address ("@" makes it an address:
 *    auth.emailSignIn), and "Forgot password?" beside the password's label opens a fifth view, "forgot": the address,
 *    "Send the link" (auth.passwordResetRequest; the same answer for every address), back to the sign-in form;
 *  - the sign-up form gets an optional e-mail field between the nickname and the password (filled:
 *    auth.emailSignUp, the address waits for its confirmation link; empty: auth.signUp as before), in a tighter layout;
 *  - the address rules are the shared ones (src/shared/email-rules.ts), with the server's reasons' texts; the e-mail
 *    field asks a phone for its e-mail keyboard ($inputMode).
 */
import { Event, KeyboardEvent, Point, Sprite, TextField } from '@flash';
import { NICKNAME_MAX, PASSWORD_MAX, SIGN_IN_NICKNAME_MAX, checkNickname, checkPassword, checkSignIn, type CredentialProblemReason } from '../../shared/auth-rules';
import { EMAIL_MAX, type EmailProblem, checkEmail, looksLikeEmail } from '../../shared/email-rules';
import * as T from '../runtime/auth-texts';
import { type Language, language, translate } from '../runtime/i18n';
import { MSG_VERSION_MISMATCH } from '../runtime/net-messages';
import { type NetBan, classifyNetFailure, netErrorCode } from '../runtime/net-policy';
import { BigButton, ERROR_COLOR, HINT_COLOR, LINK_COLOR, SmallButton, TextLink, inputField, label, popupFrame, setRawText } from './controls';
import { languageRow, languageRowWidth } from './language-switch';
import { addSplashNotice } from './legal-notice';
import { MSG_PLAY_GAMES_NOT_LINKED, MSG_PLAY_GAMES_SIGN_IN, MSG_PLAY_GAMES_SIGN_IN_FAILED } from '../runtime/monetization-ui';

export type AuthView = 'landing' | 'signIn' | 'signUp' | 'server' | 'forgot';

/**
 * The server setting a client offers (FWPDeps.serverSetting; src/client/platform wires it): the game only shows it.
 * `check` returns null for an address the client accepts, else a reason (see SERVER_PROBLEM_TEXT).
 */
export interface ServerSettingPort {
  /** The address the game is connected to (shown on the landing view). */
  readonly current: string;
  /** The address the player stored ('' = the build's default server). */
  readonly stored: string;
  check(address: string): string | null;
  /** Stores the address ('' = back to the default) and restarts the game against it. */
  apply(address: string): void;
  /** The build connects over TLS only (the Android release APK): the hint names no http:// examples. */
  readonly secureOnly?: boolean;
}

/** Characters of a server address field: host names, IPv4, ports, schemes and paths. */
export const SERVER_RESTRICT = 'A-Za-z0-9.:/_~%\\-';
/** Longest server address (src/client/config.ts SERVER_ADDRESS_MAX). */
const SERVER_MAX = 200;

/** The player card of a successful sign-in (PlayerCard of the protocol, read structurally). */
export interface AuthCard {
  playerId: string;
  nickname: string;
  level: number;
  guest: boolean;
}

export interface AuthScreenOptions {
  /** The view shown first (default: the landing view). */
  view?: AuthView;
  /** A notice of the first view (e.g. the session expired); `error` shows it in the error colour. */
  notice?: { text: string; error: boolean } | null;
  /**
   * The account signed in on another device, which ended this one's session: the sign-in form's button says "Sign in
   * here" (signing in here ends the other device's session in turn).
   */
  takeOver?: boolean;
  /** A sign-up / sign-in succeeded (the backend services keep the session). */
  onSignedIn(card: AuthCard): void;
  /** The account is banned: the game shows its ban splash. */
  onBanned(ban: NetBan): void;
  /** A language was picked on the landing view: the game stores it and restarts. */
  onLanguage(lang: Language): void;
}

/**
 * Characters the nickname fields accept (Flash `restrict`): Latin letters, digits, "_", ".", space, "-", the Cyrillic
 * block U+0400..U+045F and Ґґ - the characters of src/shared/auth-rules.ts (written as escapes: no Cyrillic literal).
 */
export const NICKNAME_RESTRICT = 'A-Za-z0-9_. \\-\u0400-\u045F\u0490\u0491';
/** The sign-in field with e-mail sign-in: a nickname or an address (its "@" and "+" too). */
export const NICKNAME_OR_EMAIL_RESTRICT = `${NICKNAME_RESTRICT}@+`;
/** Characters of an e-mail field (src/shared/email-rules.ts: the usual ones of an address). */
export const EMAIL_RESTRICT = 'A-Za-z0-9._%+@\\-';

/** The frame: menu_tired's popup frame, scaled so the panel fits between the splash logo and the bottom of the stage. */
const PANEL_SCALE = 0.8;
const FRAME_X = 372;
const FRAME_Y = 190;
/** The content column inside the frame (frame coordinates; the beanstalk decoration covers the frame's right edge). */
const COL_X = 26;
const COL_W = 270;
/** focusRect: the margin left and right of the column and the bottom of the tallest view (frame coordinates). */
const FOCUS_MARGIN = 14;
const FOCUS_BOTTOM = 350;
/** The sign-up form with its e-mail field reaches lower (three fields; see SIGN_UP_EMAIL). */
const FOCUS_BOTTOM_EMAIL = 374;
const FOCUS_BORDER = LINK_COLOR;
const IDLE_BORDER = 0x000000;

type Field = 'nickname' | 'password' | 'email';

/** The rows of a form (frame coordinates): label, field and message line of each field; status; button; last row. */
interface FormLayout {
  title: number;
  nickname: number;
  email: number | null;
  password: number;
  status: number;
  submit: number;
  lastRow: { top: number; bottom: number };
}
/**
 * A field's block: its label at `y`, the field 18 below, its two message lines 23 below the field's top; the
 * password's field 22 below its label (the show / hide toggle stands on the label's line).
 */
const FIELD_DY = 18;
const MESSAGE_DY = 41;
const PASSWORD_FIELD_DY = 22;
const PASSWORD_MESSAGE_DY = 45;
const FORM: FormLayout = { title: 20, nickname: 50, email: null, password: 129, status: 206, submit: 242, lastRow: { top: 287, bottom: FOCUS_BOTTOM } };
/** The sign-up form with the optional e-mail field: the same blocks, closer together, and the frame's lower part. */
const SIGN_UP_EMAIL: FormLayout = { title: 14, nickname: 36, email: 106, password: 180, status: 254, submit: 289, lastRow: { top: 334, bottom: FOCUS_BOTTOM_EMAIL } };

export class AuthScreen {
  /** Everything of the screen (added to the game's interface layer by FWP.net_show_auth). */
  readonly root = new Sprite();
  view: AuthView = 'landing';
  /** A sign-up / sign-in request is in flight. */
  busy = false;
  /** The form fields of the current view (null on the landing view). */
  nickname: TextField | null = null;
  password: TextField | null = null;
  /** The e-mail field: the sign-up form's optional one (e-mail sign-in on), the "forgot" view's. */
  email: TextField | null = null;
  /** The message lines under the fields and the status line of the current form (or the landing notice). */
  nicknameMessage: TextField | null = null;
  passwordMessage: TextField | null = null;
  emailMessage: TextField | null = null;
  /** "Forgot password?" of the sign-in form (e-mail sign-in on). */
  forgotLink: TextLink | null = null;
  status: TextField | null = null;
  submitButton: BigButton | null = null;
  toggleButton: SmallButton | null = null;
  backButton: SmallButton | null = null;
  switchLink: TextLink | null = null;
  /** The server view's address field and the landing view's link to it (null elsewhere / where not offered). */
  serverField: TextField | null = null;
  serverLink: TextLink | null = null;
  resetButton: SmallButton | null = null;
  /** Sign-up / sign-in requests sent (tests: no double submit). */
  requests = 0;

  private readonly content = new Sprite();
  private page: Sprite | null = null;
  private readonly errors: Record<Field, string | null> = { nickname: null, password: null, email: null };
  private readonly edited: Record<Field, boolean> = { nickname: false, password: false, email: false };
  private attempted = false;
  private disposed = false;
  private stageRef: any = null;

  constructor(
    private readonly fwp: any,
    private readonly opts: AuthScreenOptions,
  ) {
    this.root.name = '$auth_screen';
    // The loading splash's picture (the logo, the city) without its progress bar, status line and advice.
    const splash = fwp._assets[0].get('splash_loading');
    for (const part of ['bar', 'loading', 'stat_text', 'advice']) if (splash[part]) splash[part].visible = false;
    splash.mouseChildren = false;
    this.root.addChild(splash);
    const panel = this.buildPanel();
    this.root.addChild(panel);
    // The trademark notice at the bottom, as the loading splash under it has it (a new player sees this screen right
    // after the page's own loading screen: the game's splash is gone before it is drawn), clear of the form's frame.
    addSplashNotice(splash, () => [panel, this.content]);
    this.content.name = '$auth_content';
    this.content.x = FRAME_X;
    this.content.y = FRAME_Y;
    this.root.addChild(this.content);
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
    this.show(opts.view ?? 'landing', opts.notice ?? null);
  }

  /** The popup frame of menu_tired with its beanstalk and bones, without the texts, the icon and the buttons. */
  private buildPanel(): Sprite {
    const tired = popupFrame(this.fwp, FRAME_X, FRAME_Y, PANEL_SCALE);
    tired.name = '$auth_panel';
    return tired;
  }

  /**
   * The form's column in stage coordinates (every view's content with a small margin; the frame's own border may fall
   * outside), or null when the screen is not shown. A small screen (a phone) zooms into it (the browser host's
   * focusRect, src/main.ts): the whole 800x600 stage there would make the form's text a few pixels tall and its
   * fields too small to touch.
   */
  focusRect(): { x: number; y: number; width: number; height: number } | null {
    if (this.disposed || !this.root.stage) return null;
    const a = this.root.localToGlobal(new Point(FRAME_X + COL_X - FOCUS_MARGIN, FRAME_Y + 4));
    const bottom = this.view === 'signUp' && this.email ? FOCUS_BOTTOM_EMAIL : FOCUS_BOTTOM;
    const b = this.root.localToGlobal(new Point(FRAME_X + COL_X + COL_W + FOCUS_MARGIN, FRAME_Y + bottom));
    return { x: Math.round(a.x), y: Math.round(a.y), width: Math.round(b.x - a.x), height: Math.round(b.y - a.y) };
  }

  /** Starts listening to the keyboard (once the screen is on the stage). */
  attach(): void {
    const st = this.root.stage;
    if (!st || this.stageRef) return;
    this.stageRef = st;
    st.addEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
    this.focusFirst();
  }

  /** Removes the screen's listeners and empties its fields (the screen is gone for good). */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    if (this.stageRef) {
      this.stageRef.removeEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
      if (this.stageRef.focus && this.fields().includes(this.stageRef.focus)) this.stageRef.focus = null;
    }
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
    if (this.password) this.password.text = '';
    if (this.root.parent) this.root.parent.removeChild(this.root);
  }

  // ------------------------------------------------------------------------------------------------ views

  /** The sign-in form's button: "Sign in", or "Sign in here" after the account signed in on another device. */
  private signInText(): string {
    return this.opts.takeOver ? T.MSG_AUTH_SIGN_IN_HERE : T.MSG_AUTH_SIGN_IN;
  }

  /** Opens a view (the nickname typed in one form is kept for the other; the password never is). */
  show(view: AuthView, notice: { text: string; error: boolean } | null = null): void {
    if (this.busy || this.disposed) return;
    const nickname = this.nickname ? this.nickname.text : '';
    // The address typed in one view comes along (the sign-in field holds one when it has an "@").
    const email = this.email ? this.email.text : looksLikeEmail(nickname) ? nickname : '';
    if (this.page) {
      if (this.stageRef && this.fields().includes(this.stageRef.focus)) this.stageRef.focus = null;
      if (this.password) this.password.text = '';
      this.content.removeChild(this.page);
    }
    this.view = view;
    this.nickname = this.password = this.email = this.nicknameMessage = this.passwordMessage = this.emailMessage = this.status = null;
    this.submitButton = null;
    this.toggleButton = this.backButton = null;
    this.switchLink = null;
    this.forgotLink = null;
    this.serverField = null;
    this.serverLink = null;
    this.resetButton = null;
    this.errors.nickname = this.errors.password = this.errors.email = null;
    this.edited.nickname = this.edited.password = this.edited.email = false;
    this.attempted = false;
    this.page = view === 'landing' ? this.buildLanding() : view === 'server' ? this.buildServer() : view === 'forgot' ? this.buildForgot() : this.buildForm(view);
    this.page.name = `$auth_${view}`;
    this.content.addChild(this.page);
    // (The builders set the fields; TypeScript still sees the nulls assigned above.)
    const field = this.nickname as TextField | null;
    const status = this.status as TextField | null;
    // (a nickname field takes the address only where it may hold one: the sign-in form with e-mail sign-in)
    if (field && nickname && (!looksLikeEmail(nickname) || field.restrict === NICKNAME_OR_EMAIL_RESTRICT)) {
      field.text = '';
      field.replaceText(0, 0, nickname.slice(0, field.maxChars));
      this.edited.nickname = true;
    } else if (field && !nickname && email && view === 'signIn' && field.restrict === NICKNAME_OR_EMAIL_RESTRICT) {
      field.replaceText(0, 0, email.slice(0, field.maxChars));
      this.edited.nickname = true;
    }
    const mail = this.email as TextField | null;
    if (mail && email) {
      mail.replaceText(0, 0, email.slice(0, mail.maxChars));
      this.edited.email = true;
    }
    if (notice && status) {
      status.textColor = notice.error ? ERROR_COLOR : LINK_COLOR;
      status.text = notice.text;
    }
    this.refresh();
    this.focusFirst();
  }

  private buildLanding(): Sprite {
    const page = new Sprite();
    const title = label(T.MSG_AUTH_WELCOME, { size: 14, bold: true, align: 'center', width: COL_W });
    title.name = 'auth_title';
    place(page, title, COL_X, 20);
    // The backend inside the page (?backend=offline / memory) keeps the accounts in this browser, not on a server.
    const local = this.fwp.services && this.fwp.services.transportKind === 'in-process';
    const text = label(local ? T.MSG_AUTH_WELCOME_TEXT_LOCAL : T.MSG_AUTH_WELCOME_TEXT, { size: 12, align: 'center', width: COL_W, lines: 4 });
    text.name = 'auth_welcome';
    place(page, text, COL_X, 46);
    this.status = label('', { size: 12, bold: true, color: ERROR_COLOR, align: 'center', width: COL_W, lines: 2 });
    this.status.name = 'auth_status';
    place(page, this.status, COL_X, 116);
    const signIn = new BigButton(this.fwp, 'auth_sign_in', T.MSG_AUTH_SIGN_IN);
    signIn.addEventListener('click', () => this.show('signIn'));
    place(page, signIn, COL_X + (COL_W - signIn.width) / 2, 152);
    const signUp = new BigButton(this.fwp, 'auth_sign_up', T.MSG_AUTH_SIGN_UP);
    signUp.addEventListener('click', () => this.show('signUp'));
    place(page, signUp, COL_X + (COL_W - signUp.width) / 2, 202);
    // Under the language row (added first: the row's options are drawn over it).
    const setting = this.serverSetting();
    if (setting) {
      this.serverLink = new TextLink('auth_server', '', () => this.show('server'));
      setRawText(this.serverLink.field, `${translate(T.MSG_AUTH_SERVER)}: ${setting.current}`);
      if (this.serverLink.field.textWidth > COL_W - 4) setRawText(this.serverLink.field, `${translate(T.MSG_AUTH_SERVER)}: ${setting.current.slice(0, 28)}…`);
      place(page, this.serverLink, COL_X + (COL_W - this.serverLink.width) / 2, 318);
      this.serverLink.setHitRect({ x: -8, y: -2, width: this.serverLink.width + 16, height: 22 });
    }
    // Google Play Games (Android app, where configured): the account linked to this device's Play Games player.
    const playGames = this.fwp.deps && this.fwp.deps.monetization ? this.fwp.deps.monetization.playGames : null;
    if (playGames && playGames.available()) {
      const link = new TextLink('auth_play_games', MSG_PLAY_GAMES_SIGN_IN, () => {
        if (this.busy) return;
        this.busy = true;
        if (this.status) this.status.text = T.MSG_AUTH_WAIT;
        void playGames.signIn().then((r: string) => {
          this.busy = false;
          if (r !== 'signed_in' && this.status) this.status.text = r === 'not_linked' ? MSG_PLAY_GAMES_NOT_LINKED : MSG_PLAY_GAMES_SIGN_IN_FAILED;
        });
      });
      place(page, link, COL_X + (COL_W - link.width) / 2, setting ? 340 : 318);
      link.setHitRect({ x: -8, y: -2, width: link.width + 16, height: 22 });
    }
    // (Each option a finger's target down to the server link: `hit`, the row's coordinates.)
    const row = languageRow(page, (lang) => this.opts.onLanguage(lang), { x: 0, y: 262, optionsDy: 18, hit: { top: 16, bottom: 54 } });
    if (row) row.x = Math.round(COL_X + (COL_W - languageRowWidth(row)) / 2);
    return page;
  }

  /** The server offers e-mail sign-in (GameServices.emailAvailable). */
  emailOn(): boolean {
    const services = this.fwp.services;
    return !!services && typeof services.emailAvailable === 'function' && services.emailAvailable() === true;
  }

  private buildForm(view: 'signIn' | 'signUp'): Sprite {
    const page = new Sprite();
    const signUp = view === 'signUp';
    const emailOn = this.emailOn();
    const L = signUp && emailOn ? SIGN_UP_EMAIL : FORM;
    const fieldDy = FIELD_DY;
    const messageDy = MESSAGE_DY;
    const title = label(signUp ? T.MSG_AUTH_SIGN_UP_TITLE : T.MSG_AUTH_SIGN_IN_TITLE, { size: 14, bold: true, align: 'center', width: COL_W });
    title.name = 'auth_title';
    place(page, title, COL_X, L.title);

    // The sign-in field takes an address too where the server offers e-mail sign-in.
    const either = !signUp && emailOn;
    place(page, named(label(either ? T.MSG_AUTH_NICKNAME_OR_EMAIL : T.MSG_AUTH_NICKNAME, { bold: true }), 'auth_nickname_label'), COL_X, L.nickname);
    this.nickname = inputField('auth_nickname', { width: COL_W, maxChars: signUp ? NICKNAME_MAX : either ? EMAIL_MAX : SIGN_IN_NICKNAME_MAX, restrict: either ? NICKNAME_OR_EMAIL_RESTRICT : NICKNAME_RESTRICT });
    // Its Enter moves on to the next field (the on-screen keyboard's key says "next"); the password's sends the form.
    this.nickname.$enterKeyHint = 'next';
    place(page, this.nickname, COL_X, L.nickname + fieldDy);
    this.nicknameMessage = named(label('', { size: 11, color: HINT_COLOR, width: COL_W, lines: 2 }), 'auth_nickname_message');
    place(page, this.nicknameMessage, COL_X, L.nickname + messageDy);

    if (L.email !== null) {
      place(page, named(label(T.MSG_AUTH_EMAIL, { bold: true }), 'auth_email_label'), COL_X, L.email);
      this.email = inputField('auth_email', { width: COL_W, maxChars: EMAIL_MAX, restrict: EMAIL_RESTRICT });
      this.email.$enterKeyHint = 'next';
      this.email.$inputMode = 'email';
      place(page, this.email, COL_X, L.email + fieldDy);
      this.emailMessage = named(label('', { size: 11, color: HINT_COLOR, width: COL_W, lines: 2 }), 'auth_email_message');
      place(page, this.emailMessage, COL_X, L.email + messageDy);
    }

    const pwLabel = named(label(T.MSG_AUTH_PASSWORD, { bold: true }), 'auth_password_label');
    place(page, pwLabel, COL_X, L.password);
    this.toggleButton = new SmallButton(this.fwp, 'auth_toggle_password', T.MSG_AUTH_SHOW_PASSWORD);
    this.toggleButton.addEventListener('click', () => this.togglePassword());
    place(page, this.toggleButton, COL_X + COL_W - this.toggleButton.width, L.password - 4);
    // A finger's target from under the message line above to the password field, out to the column's edge.
    const top = L.password - 11;
    this.toggleButton.setHitRect({ x: -16, y: top - (L.password - 4), width: this.toggleButton.width + 16 + FOCUS_MARGIN, height: L.password + PASSWORD_FIELD_DY - 1 - top });
    if (either) {
      // "Forgot password?" between the password's label and the show / hide toggle, a finger's target of that band.
      this.forgotLink = new TextLink('auth_forgot', T.MSG_AUTH_FORGOT, () => this.show('forgot'));
      const fmt = this.forgotLink.field.defaultTextFormat;
      fmt.size = 11;
      this.forgotLink.field.defaultTextFormat = fmt;
      this.forgotLink.field.setTextFormat(fmt);
      const right = this.toggleButton.x - 16 - 6;
      const left = COL_X + pwLabel.textWidth + 10;
      place(page, this.forgotLink, Math.max(left, right - this.forgotLink.width), L.password + 1);
      // (from just under the nickname's message lines: their second line reaches down to about L.password - 10)
      const linkTop = L.password - 8;
      this.forgotLink.setHitRect({ x: left - 4 - this.forgotLink.x, y: linkTop - this.forgotLink.y, width: right + 6 - (left - 4), height: L.password + PASSWORD_FIELD_DY - 1 - linkTop });
    }
    this.password = inputField('auth_password', { width: COL_W, maxChars: PASSWORD_MAX, password: true });
    place(page, this.password, COL_X, L.password + PASSWORD_FIELD_DY);
    this.passwordMessage = named(label('', { size: 11, color: HINT_COLOR, width: COL_W, lines: 2 }), 'auth_password_message');
    place(page, this.passwordMessage, COL_X, L.password + PASSWORD_MESSAGE_DY);

    this.status = named(label('', { size: 12, bold: true, color: ERROR_COLOR, align: 'center', width: COL_W, lines: 2 }), 'auth_status');
    place(page, this.status, COL_X, L.status);
    this.submitButton = new BigButton(this.fwp, 'auth_submit', signUp ? T.MSG_AUTH_CREATE : this.signInText());
    this.submitButton.addEventListener('click', () => this.submit());
    place(page, this.submitButton, COL_X + (COL_W - this.submitButton.width) / 2, L.submit);
    // The last row: back at the left, the link to the other form right of it (on two lines where it is long), each a
    // finger's target over the whole row (touchRow).
    this.backButton = new SmallButton(this.fwp, 'auth_back', T.MSG_AUTH_BACK);
    this.backButton.addEventListener('click', () => this.show('landing'));
    this.switchLink = new TextLink('auth_switch', signUp ? T.MSG_AUTH_TO_SIGN_IN : T.MSG_AUTH_TO_SIGN_UP, () => this.show(signUp ? 'signIn' : 'signUp'));
    wrapLink(this.switchLink, COL_W - this.backButton.width - ROW_GAP);
    touchRow(page, [this.backButton, this.switchLink], L.lastRow);

    for (const [field, which] of [
      [this.nickname, 'nickname'],
      [this.email, 'email'],
      [this.password, 'password'],
    ] as const) {
      if (!field) continue;
      field.addEventListener(Event.CHANGE, () => this.onEdit(which));
      field.addEventListener('focusIn', () => this.frame());
      field.addEventListener('focusOut', () => this.frame());
    }
    return page;
  }

  /** The "forgot" view: the address, "Send the link" (auth.passwordResetRequest), back to the sign-in form. */
  private buildForgot(): Sprite {
    const page = new Sprite();
    place(page, named(label(T.MSG_AUTH_FORGOT_TITLE, { size: 14, bold: true, align: 'center', width: COL_W }), 'auth_title'), COL_X, 20);
    const text = named(label(T.MSG_AUTH_FORGOT_TEXT, { size: 12, width: COL_W, lines: 3 }), 'auth_forgot_text');
    // Three whole lines with the field's gutters.
    text.height = 52;
    place(page, text, COL_X, 46);
    place(page, named(label(T.MSG_AUTH_EMAIL, { bold: true }), 'auth_email_label'), COL_X, 104);
    this.email = inputField('auth_email', { width: COL_W, maxChars: EMAIL_MAX, restrict: EMAIL_RESTRICT });
    this.email.$inputMode = 'email';
    place(page, this.email, COL_X, 104 + FIELD_DY);
    this.emailMessage = named(label('', { size: 11, color: HINT_COLOR, width: COL_W, lines: 2 }), 'auth_email_message');
    place(page, this.emailMessage, COL_X, 104 + MESSAGE_DY);
    this.status = named(label('', { size: 12, bold: true, color: ERROR_COLOR, align: 'center', width: COL_W, lines: 3 }), 'auth_status');
    this.status.height = 52;
    place(page, this.status, COL_X, 176);
    this.submitButton = new BigButton(this.fwp, 'auth_submit', T.MSG_AUTH_FORGOT_SEND);
    this.submitButton.addEventListener('click', () => this.submit());
    place(page, this.submitButton, COL_X + (COL_W - this.submitButton.width) / 2, 242);
    this.backButton = new SmallButton(this.fwp, 'auth_back', T.MSG_AUTH_BACK);
    this.backButton.addEventListener('click', () => this.show('signIn'));
    touchRow(page, [this.backButton], LAST_ROW);
    this.email.addEventListener(Event.CHANGE, () => this.onEdit('email'));
    this.email.addEventListener('focusIn', () => this.frame());
    this.email.addEventListener('focusOut', () => this.frame());
    return page;
  }

  /** The client's server setting, when it offers one. */
  private serverSetting(): ServerSettingPort | null {
    const s = this.fwp.deps && this.fwp.deps.serverSetting;
    return s && typeof s.apply === 'function' ? (s as ServerSettingPort) : null;
  }

  /** The server view: the address field, save (restart against it), the default server, back. */
  private buildServer(): Sprite {
    const page = new Sprite();
    const setting = this.serverSetting();
    place(page, named(label(T.MSG_AUTH_SERVER, { size: 14, bold: true, align: 'center', width: COL_W }), 'auth_title'), COL_X, 20);
    place(page, named(label(T.MSG_AUTH_SERVER_ADDRESS, { bold: true }), 'auth_server_label'), COL_X, 50);
    this.serverField = inputField('auth_server_address', { width: COL_W, maxChars: SERVER_MAX, restrict: SERVER_RESTRICT });
    place(page, this.serverField, COL_X, 68);
    if (setting) setRawText(this.serverField, setting.stored || setting.current);
    const hint = setting && setting.secureOnly ? T.MSG_AUTH_SERVER_HINT_SECURE : T.MSG_AUTH_SERVER_HINT;
    place(page, named(label(hint, { size: 11, color: HINT_COLOR, width: COL_W, lines: 4 }), 'auth_server_hint'), COL_X, 94);
    this.status = named(label('', { size: 12, bold: true, color: ERROR_COLOR, align: 'center', width: COL_W, lines: 2 }), 'auth_status');
    place(page, this.status, COL_X, 160);
    this.submitButton = new BigButton(this.fwp, 'auth_server_save', T.MSG_AUTH_SERVER_SAVE);
    this.submitButton.addEventListener('click', () => this.submitServer());
    place(page, this.submitButton, COL_X + (COL_W - this.submitButton.width) / 2, 198);
    // The default server and back side by side under the button, each a finger's target over the row (touchRow).
    this.resetButton = new SmallButton(this.fwp, 'auth_server_default', T.MSG_AUTH_SERVER_DEFAULT);
    this.resetButton.addEventListener('click', () => this.applyServer(''));
    this.backButton = new SmallButton(this.fwp, 'auth_back', T.MSG_AUTH_BACK);
    this.backButton.addEventListener('click', () => this.show('landing'));
    touchRow(page, [this.resetButton, this.backButton], SERVER_ROW);
    this.serverField.addEventListener(Event.CHANGE, () => {
      if (this.status) this.status.text = '';
    });
    this.serverField.addEventListener('focusIn', () => this.frame());
    this.serverField.addEventListener('focusOut', () => this.frame());
    return page;
  }

  /** Save on the server view (Enter or the button): a refused address is explained under the field. */
  submitServer(): void {
    if (this.busy || this.disposed || this.view !== 'server' || !this.serverField) return;
    const setting = this.serverSetting();
    if (!setting) return;
    const address = this.serverField.text.trim();
    const problem = setting.check(address);
    if (problem) {
      if (this.status) {
        this.status.textColor = ERROR_COLOR;
        this.status.text = T.SERVER_PROBLEM_TEXT[problem] ?? T.SERVER_PROBLEM_TEXT.invalid;
      }
      const st = this.root.stage;
      if (st) st.focus = this.serverField;
      return;
    }
    this.applyServer(address);
  }

  private applyServer(address: string): void {
    const setting = this.serverSetting();
    if (!setting || this.busy || this.disposed) return;
    this.setBusy(true);
    if (this.status) {
      this.status.textColor = LINK_COLOR;
      this.status.text = T.MSG_AUTH_WAIT;
    }
    setting.apply(address);
  }

  /** Back (the Android back button): a form or the server view returns to the landing view; false on the landing view. */
  back(): boolean {
    if (this.disposed || this.view === 'landing') return false;
    if (!this.busy) this.show(this.view === 'forgot' ? 'signIn' : 'landing');
    return true;
  }

  // ------------------------------------------------------------------------------------------------ input

  private readonly onKey = (e: any): void => {
    if (this.disposed || this.view === 'landing') return;
    // Enter in the nickname field goes on to the password field, without judging the password not typed yet.
    const st = this.root.stage;
    const list = this.fields();
    const at = st ? list.indexOf(st.focus as TextField) : -1;
    // Enter moves on from every field before the password (without judging the fields not typed yet).
    if (e.keyCode === 13 && st && this.password && at >= 0 && list[at] !== this.password && at < list.length - 1) {
      if (!this.busy) st.focus = list[at + 1];
    } else if (e.keyCode === 13) this.submit();
    else if (e.keyCode === 9) this.focusNext(e.shiftKey ? -1 : 1);
    else if (e.keyCode === 27 && !this.busy) this.show(this.view === 'forgot' ? 'signIn' : 'landing');
  };

  private fields(): TextField[] {
    return [this.nickname, this.email, this.password, this.serverField].filter((f): f is TextField => !!f);
  }

  private focusFirst(): void {
    const st = this.root.stage;
    if (st && this.view === 'server' && this.serverField) st.focus = this.serverField;
    if (st && this.view === 'forgot' && this.email) st.focus = this.email;
    if (!st || this.view === 'landing' || !this.nickname || !this.password) return;
    st.focus = this.nickname.text.trim() !== '' ? this.password : this.nickname;
  }

  /** Tab: the next (or with Shift the previous) field of the form. */
  focusNext(step: 1 | -1 = 1): void {
    const st = this.root.stage;
    const list = this.fields();
    if (!st || !list.length || this.busy) return;
    const at = list.indexOf(st.focus as TextField);
    st.focus = list[(at + step + list.length) % list.length];
  }

  private onEdit(which: Field): void {
    this.edited[which] = true;
    // The server's objection concerns the old text (e.g. a taken nickname); the status line is about the last try.
    this.errors[which] = null;
    if (this.status) this.status.text = '';
    this.refresh();
  }

  /** The password shown in clear text or as asterisks; the field keeps the focus. */
  togglePassword(): void {
    if (!this.password || this.busy) return;
    this.password.displayAsPassword = !this.password.displayAsPassword;
    this.toggleButton?.setText(this.password.displayAsPassword ? T.MSG_AUTH_SHOW_PASSWORD : T.MSG_AUTH_HIDE_PASSWORD);
    const st = this.root.stage;
    if (st) st.focus = this.password;
  }

  // ------------------------------------------------------------------------------------------------ checks

  /** The problems of the form as the shared rules see them (sign-up: the full rules; sign-in: only what can match). */
  private problems(): Record<Field, CredentialProblemReason | EmailProblem | null> {
    const nickname = this.nickname ? this.nickname.text : '';
    const password = this.password ? this.password.text : '';
    const email = this.email ? this.email.text : '';
    const mail = (text: string) => {
      const c = checkEmail(text);
      return c.ok ? null : c.reason;
    };
    if (this.view === 'forgot') return { nickname: null, password: null, email: mail(email) };
    if (this.view === 'signUp') {
      const n = checkNickname(nickname);
      const p = checkPassword(password);
      // The address is optional: an empty field is no problem.
      return { nickname: n.ok ? null : n.reason, password: p.ok ? null : p.reason, email: this.email && email.trim() !== '' ? mail(email) : null };
    }
    // Sign-in checks each field on its own (a valid stand-in for the other one); an "@" makes the first one an address.
    const n = this.emailOn() && looksLikeEmail(nickname) ? mail(nickname) : (checkSignIn(nickname, 'x')?.reason ?? null);
    const p = checkSignIn('xxx', password);
    return { nickname: n, password: p ? p.reason : null, email: null };
  }

  /** The text of a field's problem (a nickname / password reason or an address reason). */
  private problemText(problem: CredentialProblemReason | EmailProblem): string {
    return problem in T.EMAIL_PROBLEM_TEXT ? T.EMAIL_PROBLEM_TEXT[problem as EmailProblem] : T.CREDENTIAL_PROBLEM_TEXT[problem as CredentialProblemReason];
  }

  /** Redraws the message lines: the server's objection, else the rules' problem once shown, else the hint. */
  private refresh(): void {
    if (this.view === 'landing' || this.view === 'server') return;
    const problems = this.problems();
    const signUp = this.view === 'signUp';
    const hints: Record<Field, string> = { nickname: signUp ? T.MSG_AUTH_NICKNAME_HINT : '', password: signUp ? T.MSG_AUTH_PASSWORD_HINT : '', email: signUp ? T.MSG_AUTH_EMAIL_HINT : '' };
    for (const which of ['nickname', 'email', 'password'] as const) {
      const line = which === 'nickname' ? this.nicknameMessage : which === 'email' ? this.emailMessage : this.passwordMessage;
      const field = which === 'nickname' ? this.nickname : which === 'email' ? this.email : this.password;
      if (!line || !field) continue;
      const problem = problems[which];
      // Live while typing in the sign-up form (a field is judged once it has text), and for both forms after a submit.
      const live = (signUp && this.edited[which] && field.text !== '') || this.attempted;
      const text = this.errors[which] ?? (problem && live ? this.problemText(problem) : hints[which]);
      line.textColor = this.errors[which] || (problem && live) ? ERROR_COLOR : HINT_COLOR;
      line.text = text;
    }
  }

  // ------------------------------------------------------------------------------------------------ submit

  /** Sends the form (Enter or the submit button): checked by the shared rules first, one request at a time. */
  submit(): void {
    if (this.view === 'server') return this.submitServer();
    if (this.view === 'forgot') return this.submitForgot();
    if (this.busy || this.disposed || this.view === 'landing' || !this.nickname || !this.password) return;
    this.attempted = true;
    this.errors.nickname = this.errors.password = this.errors.email = null;
    if (this.status) this.status.text = '';
    const problems = this.problems();
    if (problems.nickname || problems.email || problems.password) {
      this.refresh();
      const st = this.root.stage;
      if (st) st.focus = problems.nickname ? this.nickname : problems.email ? this.email : this.password;
      return;
    }
    const nickname = this.nickname.text;
    const email = this.email ? this.email.text.trim() : '';
    // The password leaves the field for the one request and is not kept anywhere else.
    const password = this.password.text;
    this.password.text = '';
    // The emptied password field is not a mistake of the player: its line shows the hint again until the next try.
    this.attempted = false;
    this.edited.password = false;
    this.setBusy(true);
    this.refresh();
    this.requests++;
    const services = this.fwp.services;
    const lang = language();
    const request: Promise<AuthCard> =
      this.view === 'signUp'
        ? email !== ''
          ? services.signUpWithEmail(nickname, email, password, lang)
          : services.signUp(nickname, password)
        : this.emailOn() && looksLikeEmail(nickname)
          ? services.signInWithEmail(nickname, password)
          : services.signIn(nickname, password);
    Promise.resolve(request).then(
      (card) => this.succeeded(card),
      (error) => this.failed(error),
    );
  }

  /** "Send the link" of the forgot view: the same answer for every address (no account enumeration). */
  submitForgot(): void {
    if (this.busy || this.disposed || this.view !== 'forgot' || !this.email) return;
    this.attempted = true;
    this.errors.email = null;
    if (this.status) this.status.text = '';
    const problem = this.problems().email;
    if (problem) {
      this.refresh();
      const st = this.root.stage;
      if (st) st.focus = this.email;
      return;
    }
    const email = this.email.text.trim();
    this.setBusy(true);
    this.requests++;
    Promise.resolve(this.fwp.services.requestPasswordReset(email, language())).then(
      () => {
        if (this.disposed) return;
        this.setBusy(false);
        if (this.status) {
          this.status.textColor = LINK_COLOR;
          this.status.text = T.MSG_AUTH_FORGOT_SENT;
        }
      },
      (error: any) => {
        if (this.disposed) return;
        this.setBusy(false);
        const code = netErrorCode(error);
        const reason = error && error.details && typeof error.details.reason === 'string' ? error.details.reason : '';
        if (code === 'VALIDATION_FAILED' && reason in T.EMAIL_PROBLEM_TEXT) {
          this.errors.email = T.EMAIL_PROBLEM_TEXT[reason as EmailProblem];
          this.refresh();
          return;
        }
        if (this.status) {
          this.status.textColor = ERROR_COLOR;
          this.status.text = reason === 'email_send_limit' ? T.MSG_AUTH_EMAIL_LIMIT : failureText(code, reason);
        }
      },
    );
  }

  private succeeded(card: AuthCard): void {
    if (this.disposed) return;
    this.busy = false;
    this.opts.onSignedIn(card);
  }

  private failed(error: any): void {
    if (this.disposed) return;
    this.setBusy(false);
    const code = netErrorCode(error);
    const details = error && error.details ? error.details : {};
    const reason = typeof details.reason === 'string' ? details.reason : '';
    const st = this.root.stage;
    if (code === 'BANNED') {
      const failure = classifyNetFailure(error, 0, 0);
      if (failure.action === 'banned') {
        this.opts.onBanned(failure.ban);
        return;
      }
    }
    if (code === 'VALIDATION_FAILED' && reason in T.EMAIL_PROBLEM_TEXT) {
      // The sign-in form's first field holds the address there; the sign-up form has its own e-mail field.
      const which: Field = this.email ? 'email' : 'nickname';
      this.errors[which] = T.EMAIL_PROBLEM_TEXT[reason as EmailProblem];
      if (st) st.focus = which === 'email' ? this.email : this.nickname;
    } else if (code === 'UNAUTHORIZED' && reason === 'invalid_credentials' && this.view === 'signIn' && this.nickname && looksLikeEmail(this.nickname.text)) {
      if (this.status) {
        this.status.textColor = ERROR_COLOR;
        this.status.text = T.MSG_AUTH_INVALID_EMAIL_CREDENTIALS;
      }
      if (st) st.focus = this.password;
    } else if (code === 'VALIDATION_FAILED' && reason in T.CREDENTIAL_PROBLEM_TEXT) {
      const which: Field = details.field === 'password' || reason.startsWith('password_') ? 'password' : 'nickname';
      this.errors[which] = T.CREDENTIAL_PROBLEM_TEXT[reason as CredentialProblemReason];
      if (st) st.focus = which === 'nickname' ? this.nickname : this.password;
    } else if (code === 'CONFLICT' && reason === 'nickname_taken') {
      this.errors.nickname = T.MSG_AUTH_NICKNAME_TAKEN;
      if (st) st.focus = this.nickname;
    } else {
      if (this.status) {
        this.status.textColor = ERROR_COLOR;
        this.status.text = failureText(code, reason);
      }
      if (st) st.focus = this.password;
    }
    this.refresh();
  }

  private setBusy(on: boolean): void {
    this.busy = on;
    if (this.submitButton) {
      this.submitButton.enabled = !on;
      const idle = this.view === 'server' ? T.MSG_AUTH_SERVER_SAVE : this.view === 'forgot' ? T.MSG_AUTH_FORGOT_SEND : this.view === 'signUp' ? T.MSG_AUTH_CREATE : this.signInText();
      this.submitButton.setText(on ? T.MSG_AUTH_WAIT : idle);
    }
    for (const b of [this.toggleButton, this.backButton, this.resetButton]) if (b) b.enabled = !on;
    for (const link of [this.switchLink, this.forgotLink]) {
      if (!link) continue;
      link.mouseEnabled = !on;
      link.alpha = on ? 0.55 : 1;
    }
    for (const f of this.fields()) {
      f.type = on ? 'dynamic' : 'input';
      f.selectable = !on;
    }
    const st = this.root.stage;
    if (on && st && this.fields().includes(st.focus as TextField)) st.focus = null;
  }

  private readonly onFrame = (): void => this.frame();

  /** Every frame: the focused field gets the green frame. */
  private frame(): void {
    const st = this.root.stage;
    for (const f of this.fields()) {
      const color = st && st.focus === f ? FOCUS_BORDER : IDLE_BORDER;
      if (f.borderColor !== color) f.borderColor = color;
    }
  }
}

/** The status line for a refused or failed request. */
export function failureText(code: string, reason: string): string {
  switch (code) {
    case 'UNAUTHORIZED':
      return reason === 'invalid_credentials' ? T.MSG_AUTH_INVALID_CREDENTIALS : T.MSG_AUTH_FAILED;
    case 'RATE_LIMITED':
      return reason === 'too_many_attempts' ? T.MSG_AUTH_TOO_MANY_ATTEMPTS : reason === 'too_many_accounts' ? T.MSG_AUTH_TOO_MANY_ACCOUNTS : reason === 'email_send_limit' ? T.MSG_AUTH_EMAIL_LIMIT : T.MSG_AUTH_BUSY;
    case 'UNAVAILABLE':
      return T.MSG_AUTH_OFFLINE;
    case 'INTERNAL':
      return T.MSG_AUTH_SERVER_ERROR;
    case 'FORBIDDEN':
      return reason === 'feature_disabled' ? T.MSG_AUTH_DISABLED : T.MSG_AUTH_FAILED;
    case 'VERSION_MISMATCH':
      return MSG_VERSION_MISMATCH;
    default:
      return T.MSG_AUTH_FAILED;
  }
}

/** Room between the controls of a row (frame coordinates). */
const ROW_GAP = 12;
/**
 * The forms' last row (back, the link to the other form) and the server view's (the default server, back): from the
 * button above it to the zoomed view's bottom (FOCUS_BOTTOM), so that each control's target is the row's whole height
 * (about 76 CSS px on a phone: the sign-in screen is zoomed to 1.19 CSS px per stage px there).
 */
const LAST_ROW = { top: 287, bottom: FOCUS_BOTTOM };
const SERVER_ROW = { top: 243, bottom: 305 };

/** A link whose text may take two lines of `width` (right-aligned: the end of a row). */
function wrapLink(link: TextLink, width: number): void {
  const tf = link.field;
  const fmt = tf.defaultTextFormat;
  fmt.align = 'right';
  tf.defaultTextFormat = fmt;
  tf.setTextFormat(fmt);
  tf.autoSize = 'none';
  tf.wordWrap = true;
  tf.multiline = true;
  tf.width = width;
  tf.height = tf.textHeight + 4;
}

/**
 * Controls side by side in a row of the column (centred together; a row's last control that fills the rest ends at
 * the column's right edge), each in the row's vertical middle, and each taking the taps of its share of the row: its
 * own column of it, from the row's top to its bottom, split half way between neighbours (the fingers' targets of a
 * phone, larger than the art of the small buttons and the links).
 */
function touchRow(page: Sprite, controls: Array<SmallButton | TextLink>, row: { top: number; bottom: number }): void {
  const widths = controls.map((c) => (c instanceof TextLink ? c.field.width : c.width));
  const total = widths.reduce((a, w) => a + w, 0) + ROW_GAP * (controls.length - 1);
  let x = COL_X + Math.max(0, (COL_W - total) / 2);
  const mid = (row.top + row.bottom) / 2;
  const xs: number[] = [];
  controls.forEach((c, i) => {
    const h = c instanceof TextLink ? c.field.height : c.height;
    place(page, c, x, mid - h / 2);
    xs.push(Math.round(x));
    x += widths[i] + ROW_GAP;
  });
  controls.forEach((c, i) => {
    const left = i === 0 ? COL_X - FOCUS_MARGIN : (xs[i - 1] + widths[i - 1] + xs[i]) / 2;
    const right = i === controls.length - 1 ? COL_X + COL_W + FOCUS_MARGIN : (xs[i] + widths[i] + xs[i + 1]) / 2;
    c.setHitRect({ x: left - c.x, y: row.top - c.y, width: right - left, height: row.bottom - row.top });
  });
}

function place(parent: Sprite, child: any, x: number, y: number): void {
  child.x = Math.round(x);
  child.y = Math.round(y);
  parent.addChild(child);
}

function named<O extends { name: string }>(o: O, name: string): O {
  o.name = name;
  return o;
}
